(() => {
  if (customElements.get('vh-sofa-reviews')) return;
  const translate = text => window.VHi18n?.dictionary?.[text] ?? text;
  const previewLocale = window.VHi18n?.locale || document.documentElement.lang || 'en-US';
  const TOPICS = {claw:'Adjustable claw',starter:'Magnetic starter',grip:'Grip & handling',everyday:'Everyday projects'};
  const classify = review => { for(const text of [review.title,review.body]) { if(/claw|pull|angle|reclaim|salvag/i.test(text))return 'claw'; if(/magnet|starter|first.tap|starting|slot/i.test(text))return 'starter'; if(/\b(?:grip\w*|handle\w*|hold\w*|hands?|comfort\w*|feel\w*)\b/i.test(text))return 'grip'; } return 'everyday'; };
  const PREFIX = 'vh-sofa-reviews__';
  const PAGE_SIZE = 4;
  const SHORT_LENGTH = 260;
  const dateFormatter = new Intl.DateTimeFormat(previewLocale, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = PREFIX + className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  class VarnholtSofaReviews extends HTMLElement {
    connectedCallback() {
      this.events?.abort(); this.observer?.disconnect();
      this.labels = JSON.parse(this.querySelector('[data-review-labels]').textContent);
      this.generation = (this.generation || 0) + 1;
      this.topic = this.topic || 'all';
      this.limit = this.limit || PAGE_SIZE;
      this.fullBodies = this.fullBodies || new WeakMap();
      this.events = new AbortController();
      this.grid = this.querySelector('[data-review-grid]');
      this.status = this.querySelector('[data-review-status]');
      this.more = this.querySelector('[data-review-more]');
      this.retry = this.querySelector('[data-review-retry]');
      this.filters = this.querySelector('[data-review-filters]');
      this.empty = this.querySelector('[data-review-empty]');
      if (!this.grid) return;
      this.addEventListener('click', event => this.onClick(event), { signal: this.events.signal });
      this.enhanceCards(this.grid);
      if (this.entries) {
        this.showLoadedControls();
        this.updateStatus();
        return;
      }
      if ('IntersectionObserver' in window) {
        const generation = this.generation;
        this.observer = new IntersectionObserver(entries => {
          if (generation === this.generation && entries.some(entry => entry.isIntersecting)) {
            this.observer.disconnect();
            this.loadReviews();
          }
        }, { rootMargin: '360px 0px' });
        this.observer.observe(this);
      } else {
        this.loadReviews();
      }
    }

    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.events?.abort();
      this.request?.abort();
      this.observer?.disconnect();
      this.loading = false;
    }

    validData(data) {
      if (!data || data.version !== 1 || data.product_handle !== this.dataset.productHandle || !Array.isArray(data.reviews) || !Number.isInteger(data.count) || data.count !== 2381 || data.count !== data.reviews.length || !Number.isFinite(data.average) || data.average < 1 || data.average > 5) return false;
      const ids = new Set();
      let sum = 0;
      for (const review of data.reviews) {
        if (!review || typeof review.id !== 'string' || !/^[a-zA-Z0-9_-]{1,100}$/.test(review.id) || ids.has(review.id) || !['name', 'title', 'body'].every(key => typeof review[key] === 'string' && review[key].trim()) || !Number.isInteger(review.rating) || review.rating < 1 || review.rating > 5 || typeof review.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(review.date)) return false;
        const date = new Date(review.date + 'T00:00:00Z');
        if (!Number.isFinite(date.valueOf()) || date.toISOString().slice(0, 10) !== review.date) return false;
        ids.add(review.id);
        sum += review.rating;
      }
      return Math.abs(data.average - sum / data.count) < 0.051;
    }

    async loadReviews() {
      if (this.loading || this.entries || !this.isConnected) return;
      this.loading = true;
      this.request = new AbortController();
      const generation = this.generation;
      const request = this.request;
      if (this.retry) this.retry.hidden = true;
      if (this.status) this.status.textContent = this.labels.loading;
      try {
        const response = await fetch(this.dataset.reviewsSrc, { signal: request.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Review file unavailable');
        const data = await response.json();
        if (!this.validData(data)) throw new Error('Invalid review file');
        if (generation !== this.generation || !this.isConnected) return;
        this.entries = data.reviews.map(review => ({...review, topic:classify(review)}));
        this.querySelectorAll('[data-review-average]').forEach(node => { node.textContent = (data.reviews.reduce((sum, review) => sum + review.rating, 0) / data.count).toFixed(1); });
        this.querySelectorAll('[data-review-count]').forEach(node => { node.textContent = data.count.toLocaleString(previewLocale); });
        this.showLoadedControls();
        this.render(false);
      } catch (error) {
        if (generation !== this.generation || request.signal.aborted || !this.isConnected) return;
        if (this.status) this.status.textContent = this.labels.error;
        if (this.retry) this.retry.hidden = false;
      } finally {
        if (generation === this.generation) this.loading = false;
      }
    }

    showLoadedControls() {
      if (this.filters) this.filters.hidden = false;
      if (this.retry) this.retry.hidden = true;
    }

    selectedEntries() {
      return this.topic === 'all' ? this.entries : this.entries.filter(review => review.topic === this.topic);
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-review-expand')) {
        const body = button.previousElementSibling;
        const fullText = this.fullBodies.get(body);
        if (fullText === undefined) return;
        const expanded = button.getAttribute('aria-expanded') === 'true';
        body.textContent = expanded ? this.shortText(fullText) : fullText;
        button.setAttribute('aria-expanded', String(!expanded));
        button.textContent = expanded ? this.labels.expand : this.labels.collapse;
      } else if (button.hasAttribute('data-review-retry')) {
        this.loadReviews();
      } else if (button.hasAttribute('data-review-filter') && this.entries) {
        const topic = button.dataset.reviewFilter;
        if (topic !== 'all' && !Object.prototype.hasOwnProperty.call(TOPICS, topic)) return;
        this.topic = topic;
        this.limit = PAGE_SIZE;
        this.querySelectorAll('[data-review-filter]').forEach(filter => filter.setAttribute('aria-pressed', String(filter === button)));
        this.render(false);
      } else if (button.hasAttribute('data-review-more') && this.entries) {
        const oldCount = this.grid.childElementCount;
        this.limit += PAGE_SIZE;
        this.render(true);
        if (this.more.hidden) {
          const heading = this.grid.children[oldCount]?.querySelector('h3');
          if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
        }
      }
    }

    render(append) {
      const selected = this.selectedEntries();
      const start = append ? this.grid.childElementCount : 0;
      const fragment = document.createDocumentFragment();
      selected.slice(start, this.limit).forEach(review => fragment.append(this.card(review)));
      if (append) this.grid.append(fragment); else this.grid.replaceChildren(fragment);
      this.enhanceCards(this.grid);
      this.updateStatus();
    }

    updateStatus() {
      const selected = this.selectedEntries();
      const shown = Math.min(this.limit, selected.length);
      if (this.more) this.more.hidden = shown >= selected.length;
      if (this.empty) this.empty.hidden = selected.length !== 0;
      if (this.status) this.status.textContent = this.labels.progress.replaceAll('[shown]',shown.toLocaleString(previewLocale)).replaceAll('[total]',selected.length.toLocaleString(previewLocale));
    }

    card(review) {
      const article = make('article', 'card');
      article.dataset.reviewCard = '';
      article.dataset.reviewTopic = review.topic;
      article.dataset.reviewId = review.id;
      const top = make('div', 'card-top');
      const stars = make('span', 'stars', '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating));
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', this.labels.rating.replaceAll('[rating]',review.rating));
      top.append(stars, make('span', 'topic', this.labels.topics[review.topic]));
      const body = make('p', 'body', translate(review.body));
      body.dataset.reviewBody = '';
      const titleId = this.getAttribute('aria-labelledby') || 'sofa-customer-reviews';
      body.id = `${titleId}-body-${review.id}`;
      const footer = make('footer');
      const date = make('time', '', dateFormatter.format(new Date(review.date + 'T00:00:00Z')));
      date.dateTime = review.date;
      footer.append(make('strong', '', review.name), date);
      article.append(top, make('h3', '', translate(review.title)), body, footer);
      return article;
    }

    shortText(text) {
      const cut = text.slice(0, SHORT_LENGTH);
      const space = cut.lastIndexOf(' ');
      return cut.slice(0, space > SHORT_LENGTH * .65 ? space : SHORT_LENGTH).trimEnd() + '…';
    }

    enhanceCards(container) {
      container.querySelectorAll('[data-review-body]').forEach((body, index) => {
        if (body.nextElementSibling?.hasAttribute('data-review-expand') || body.textContent.length <= SHORT_LENGTH) return;
        const fullText = body.textContent;
        this.fullBodies.set(body, fullText);
        if (!body.id) body.id = `${this.getAttribute('aria-labelledby') || 'sofa-customer-reviews'}-initial-body-${index}`;
        body.textContent = this.shortText(fullText);
        const toggle = make('button', 'expand', this.labels.expand);
        toggle.type = 'button';
        toggle.dataset.reviewExpand = '';
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-controls', body.id);
        body.after(toggle);
      });
    }
  }
  customElements.define('vh-sofa-reviews', VarnholtSofaReviews);
})();
